import React, { useMemo, useState } from 'react';
import AiCommentArrow from '../../assets/icons/ai-comment-arrow.svg';
import AiFactPositive from '../../assets/icons/ai-fact-positive.svg';
import AiFactNegative from '../../assets/icons/ai-fact-negative.svg';

interface RelevanceBlockProps {
	score: number | null;
	matchLevel: string | null;
	feedback: string | null;
	className?: string;
}

// Парсит feedback: фронт шлёт либо строку, либо JSON с {summary, positiveFacts, negativeFacts}.
function parseFeedback(feedback: string | null): {
	summary: string | null;
	positiveFacts: string[];
	negativeFacts: string[];
} | null {
	if (!feedback) return null;

	try {
		const parsed = JSON.parse(feedback);
		if (parsed && typeof parsed === 'object') {
			return {
				summary: typeof parsed.summary === 'string' ? parsed.summary : null,
				positiveFacts: Array.isArray(parsed.positiveFacts)
					? parsed.positiveFacts.filter((f: unknown): f is string => typeof f === 'string')
					: [],
				negativeFacts: Array.isArray(parsed.negativeFacts)
					? parsed.negativeFacts.filter((f: unknown): f is string => typeof f === 'string')
					: [],
			};
		}
	} catch {
		// feedback — просто строка, не JSON
	}

	return { summary: feedback, positiveFacts: [], negativeFacts: [] };
}

// Пороги 75/50 — те же, что в ResultsTable.getRatingBadgeStyle (дизайн в поиске).
function getRatingBadgeStyle(rating: number): { bg: string; text: string } {
	if (rating >= 75) return { bg: '#CBF5E5', text: '#176448' };
	if (rating >= 50) return { bg: '#FBDFB1', text: '#693D11' };
	return { bg: '#FECACA', text: '#991B1B' };
}

function getRatingLabel(rating: number): string {
	if (rating >= 75) return 'Высокое соответствие';
	if (rating >= 50) return 'Среднее соответствие';
	return 'Низкое соответствие';
}

const RelevanceBlock: React.FC<RelevanceBlockProps> = ({ score, feedback, className }) => {
	const [expanded, setExpanded] = useState(false);
	const parsed = useMemo(() => parseFeedback(feedback), [feedback]);

	// Снапшот может быть пустым для старых рассылок — не показываем блок вообще.
	if (typeof score !== 'number') {
		return null;
	}

	const badge = getRatingBadgeStyle(score);
	const label = getRatingLabel(score);

	const hasExpandableContent = !!parsed && (
		!!parsed.summary ||
		parsed.positiveFacts.length > 0 ||
		parsed.negativeFacts.length > 0
	);

	return (
		<div className={className}>
			<div
				className="rounded-lg p-2 transition-all duration-200"
				style={{ backgroundColor: '#F6F8FA', cursor: hasExpandableContent ? 'pointer' : 'default' }}
				onClick={() => hasExpandableContent && setExpanded(v => !v)}
			>
				<div className="flex items-center gap-3">
					<div className="flex items-center gap-2 flex-1 min-w-0">
						<span
							className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-sm font-medium shrink-0"
							style={{ backgroundColor: badge.bg, color: badge.text }}
						>
							{score}%
						</span>
						<span className="text-xs font-medium text-black truncate">{label}</span>
					</div>
					{hasExpandableContent && (
						<img
							src={AiCommentArrow}
							alt=""
							className={`w-5 h-5 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
						/>
					)}
				</div>
				<div
					className={`overflow-hidden transition-all duration-300 ease-in-out ${
						expanded && hasExpandableContent ? 'max-h-[9999px] mt-2 pt-2 border-t border-gray-200' : 'max-h-0'
					}`}
				>
					{parsed && (
						<div className="space-y-2">
							{parsed.positiveFacts.length > 0 && (
								<ul className="space-y-1.5">
									{parsed.positiveFacts.map((fact, idx) => (
										<li
											key={`pos-${idx}`}
											className="flex items-start gap-2 text-xs text-green-700 leading-[16px]"
										>
											<img src={AiFactPositive} alt="" className="mt-0.5 h-5 w-5 flex-shrink-0" />
											<span className="break-words">{fact}</span>
										</li>
									))}
								</ul>
							)}
							{parsed.negativeFacts.length > 0 && (
								<ul className="space-y-1.5">
									{parsed.negativeFacts.map((fact, idx) => (
										<li
											key={`neg-${idx}`}
											className="flex items-start gap-2 text-xs text-red-700 leading-[16px]"
										>
											<img src={AiFactNegative} alt="" className="mt-0.5 h-5 w-5 flex-shrink-0" />
											<span className="break-words">{fact}</span>
										</li>
									))}
								</ul>
							)}
							{parsed.summary && parsed.positiveFacts.length === 0 && parsed.negativeFacts.length === 0 && (
								<p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap break-words">
									{parsed.summary}
								</p>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default RelevanceBlock;
